import { Breadcrumb, Button, Divider, Flex, Tooltip, Typography } from 'antd';
import { EditOutlined, LeftOutlined } from '@ant-design/icons';
import { useFlags } from 'launchdarkly-react-client-sdk';

import FundingAccountStep from '../../FundingAccountStep';
import OfferSetupGrid from '../OfferSetupGrid';
import StepProgressList from '../StepProgressList';
import ThirdPartyAlerts from '../ThirdPartyAlerts';

import PrecoOfferCard from './PrecoOfferCard';
import PrecoPaymentPlanDetailsStep from './PrecoPaymentPlanDetailsStep';
import PrecoReviewScheduleStep from './PrecoReviewScheduleStep';
import PrecoDisclosureStep from './PrecoDisclosureStep';
import PrecoEFTAStep from './PrecoEFTAStep';
import {
  PRECO_STEPS,
  getPrecoProgressSteps,
  usePrecoState,
  setNextStep,
  goToPreviousStep,
  setFundingAccount,
  resetPrecoState,
} from './precoState';

const { Title } = Typography;

const PrecoOfferSetup = ({ offers, outstandingBalance, thirdParty }) => {
  const { enablePrecoPaymentPlans } = useFlags();
  const {
    currentStep,
    previousStep,
    stepIndex,
    fundingAccount,
    offer,
    enrollmentType,
  } = usePrecoState();

  const handleFundingAccountContinue = () => {
    setNextStep(PRECO_STEPS.PAYMENT_PLAN_DETAILS);
  };

  const renderStep = () => {
    switch (currentStep.key) {
      case 'CHOOSE_OFFER':
        return (
          <>
            <Title level={4}>Choose An Offer</Title>
            <Flex
              gap="var(--spacing-lg)"
              style={{ marginBottom: 'var(--spacing-sm)' }}
            >
              {offers?.map((offer) => (
                <PrecoOfferCard
                  key={offer.offerId}
                  offer={offer}
                  outstandingBalance={outstandingBalance}
                />
              ))}
            </Flex>
          </>
        );

      case 'FUNDING_ACCOUNT':
        return (
          <FundingAccountStep
            fundingAccount={fundingAccount}
            setFundingAccount={setFundingAccount}
            onContinue={handleFundingAccountContinue}
          />
        );

      case 'PAYMENT_PLAN_DETAILS':
        return <PrecoPaymentPlanDetailsStep />;

      case 'REVIEW_SCHEDULE':
        return <PrecoReviewScheduleStep />;

      case 'DISCLOSURE':
        return <PrecoDisclosureStep />;

      case 'EFTA':
        return <PrecoEFTAStep />;

      default:
        return null;
    }
  };

  const showSidebar = stepIndex >= 1;

  const content = <div>{renderStep()}</div>;

  return (
    <div style={{ padding: 'var(--spacing-md)' }}>
      <ThirdPartyAlerts thirdParty={thirdParty} />
      {previousStep && (
        <Breadcrumb
          style={{ marginBottom: 'var(--spacing-md)' }}
          items={[
            {
              title: (
                <a onClick={goToPreviousStep}>
                  <LeftOutlined /> {previousStep?.label}
                </a>
              ),
            },
            { title: currentStep.label },
          ]}
        />
      )}

      {showSidebar ? (
        <OfferSetupGrid>
          <div
            style={{ position: 'relative', paddingRight: 'var(--spacing-xs)' }}
          >
            <Flex justify="space-between" align="baseline">
              <Title level={5}>
                {enablePrecoPaymentPlans
                  ? 'Payment Plan Setup'
                  : 'Offer Enrollment'}
              </Title>
              <Tooltip title="Start over from offer selection">
                <Button
                  icon={<EditOutlined />}
                  type="text"
                  onClick={() => resetPrecoState()}
                >
                  Start Over
                </Button>
              </Tooltip>
            </Flex>
            <StepProgressList
              steps={getPrecoProgressSteps(enrollmentType)}
              stepIndex={stepIndex}
            />
            <Divider
              orientation="vertical"
              style={{
                position: 'absolute',
                right: 'calc(-0.5 * var(--spacing) - var(--spacing-xs))',
                top: 0,
                height: '100%',
                margin: '0 var(--spacing-xs)',
              }}
            />
          </div>
          {content}
        </OfferSetupGrid>
      ) : (
        content
      )}
    </div>
  );
};

export default PrecoOfferSetup;
